import React from 'react'
import Select, { MultiValue, SingleValue } from 'react-select'
import styled from 'styled-components'
import { defs } from 'components/Shared/defs'
import { device } from 'components/Shared/device'
import {
  JET_ALERT_DATE_FIELD_LABELS,
  JET_ALERT_SORT_BY_LABELS,
  JET_ALERT_TYPE_OPTIONS,
  JET_ALERT_STATUS_OPTIONS,
  JET_ALERT_DATE_FIELD_VALUES,
  JET_ALERT_SORT_BY_VALUES,
  JetAlertDateField,
  JetAlertSortBy,
  JetAlertSortDir,
  JetAlertStatus,
  JetAlertType,
} from 'pages/Jet/shared/constants/alertType'
import { useJetAlertsFiltersQueryState } from '../hooks/useJetAlertsFiltersQueryState'

type JetAlertsFilters = ReturnType<typeof useJetAlertsFiltersQueryState>[0]

type AlertTypeOption = { label: string; value: JetAlertType }
type StatusOption = { label: string; value: JetAlertStatus }
type DateFieldOption = { label: string; value: JetAlertDateField }
type NumberOption = { label: string; value: number }
type SortByOption = { label: string; value: JetAlertSortBy }
type SortDirOption = { label: string; value: JetAlertSortDir }

const DAYS_AGO_OPTIONS: NumberOption[] = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 14 days', value: 14 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
]

const DATE_FIELD_OPTIONS: DateFieldOption[] = JET_ALERT_DATE_FIELD_VALUES.map(
  (value) => ({ value, label: JET_ALERT_DATE_FIELD_LABELS[value] })
)

const SORT_BY_OPTIONS: SortByOption[] = JET_ALERT_SORT_BY_VALUES.map(
  (value) => ({ value, label: JET_ALERT_SORT_BY_LABELS[value] })
)

const SORT_DIR_OPTIONS: SortDirOption[] = [
  { label: 'Newest first', value: 'desc' },
  { label: 'Oldest first', value: 'asc' },
]

const findSelectedAlertTypes = (values: JetAlertType[] | null) =>
  values ? JET_ALERT_TYPE_OPTIONS.filter((o) => values.includes(o.value)) : []

const toAlertTypeValues = (selected: MultiValue<AlertTypeOption>) =>
  selected.length ? selected.map((o) => o.value) : null

type JetAlertsFilterBarProps = {
  filters: JetAlertsFilters
  setFilters: (update: Partial<JetAlertsFilters>) => void
}

export const JetAlertsFilterBar = ({
  filters,
  setFilters,
}: JetAlertsFilterBarProps) => {
  const selectedStatus =
    JET_ALERT_STATUS_OPTIONS.find((o) => o.value === filters.status) ?? null
  const selectedDateField =
    DATE_FIELD_OPTIONS.find((o) => o.value === filters.dateField) ?? null
  const selectedDaysAgo =
    DAYS_AGO_OPTIONS.find((o) => o.value === filters.daysAgo) ?? null
  const selectedSortBy =
    SORT_BY_OPTIONS.find((o) => o.value === filters.sortBy) ?? null
  const selectedSortDir =
    SORT_DIR_OPTIONS.find((o) => o.value === filters.sortDir) ?? null

  return (
    <FilterBarWrapper showBorderTop showBorderBottom>
      <SelectGroup>
        <Select
          isMulti
          isClearable
          isSearchable={false}
          options={JET_ALERT_TYPE_OPTIONS}
          styles={wideStyles}
          name="alertTypes"
          placeholder="Alert type"
          value={findSelectedAlertTypes(filters.alertTypes)}
          onChange={(selected: MultiValue<AlertTypeOption>) =>
            setFilters({ alertTypes: toAlertTypeValues(selected) })
          }
          closeMenuOnSelect={false}
        />
        <Select
          isSearchable={false}
          options={JET_ALERT_STATUS_OPTIONS}
          styles={customStyles}
          name="status"
          placeholder="Status"
          value={selectedStatus}
          onChange={(selected: SingleValue<StatusOption>) => {
            if (selected) {
              setFilters({ status: selected.value })
            }
          }}
        />
        <Select
          isSearchable={false}
          options={DATE_FIELD_OPTIONS}
          styles={customStyles}
          name="dateField"
          placeholder="Date field"
          value={selectedDateField}
          onChange={(selected: SingleValue<DateFieldOption>) => {
            if (selected) {
              setFilters({ dateField: selected.value })
            }
          }}
        />
        <Select
          isSearchable={false}
          options={DAYS_AGO_OPTIONS}
          styles={customStyles}
          name="daysAgo"
          placeholder="Time range"
          value={selectedDaysAgo}
          onChange={(selected: SingleValue<NumberOption>) => {
            if (selected) {
              setFilters({ daysAgo: selected.value })
            }
          }}
        />
        <Select
          isSearchable={false}
          options={SORT_BY_OPTIONS}
          styles={customStyles}
          name="sortBy"
          placeholder="Sort by"
          value={selectedSortBy}
          onChange={(selected: SingleValue<SortByOption>) => {
            if (selected) {
              setFilters({ sortBy: selected.value })
            }
          }}
        />
        <Select
          isSearchable={false}
          options={SORT_DIR_OPTIONS}
          styles={customStyles}
          name="sortDir"
          placeholder="Order"
          value={selectedSortDir}
          onChange={(selected: SingleValue<SortDirOption>) => {
            if (selected) {
              setFilters({ sortDir: selected.value })
            }
          }}
        />
      </SelectGroup>
    </FilterBarWrapper>
  )
}

const customStyles = {
  container: (provided: Record<string, unknown>) => ({
    ...provided,
    minWidth: 180,
    marginRight: defs.marginM,
    marginTop: defs.marginXS,
    marginBottom: defs.marginXS,
    fontSize: defs.fontS,
  }),
}

const wideStyles = {
  container: (provided: Record<string, unknown>) => ({
    ...provided,
    minWidth: 260,
    marginRight: defs.marginM,
    marginTop: defs.marginXS,
    marginBottom: defs.marginXS,
    fontSize: defs.fontS,
  }),
}

const FilterBarWrapper = styled.div<{
  showBorderTop?: boolean
  showBorderBottom?: boolean
}>`
  border-bottom: ${(props) => (props.showBorderBottom ? '1px solid' : null)};
  border-top: ${(props) => (props.showBorderTop ? '1px solid' : null)};
  border-color: ${defs.veryLightBlack};
  padding: ${defs.paddingS};
  display: flex;
  flex-direction: row;
  align-items: center;
  background-color: #fafafa;
  @media ${device.smallerThanMobileL} {
    font-size: ${defs.fontS};
    height: 100%;
    padding-left: ${defs.paddingM};
  }
`

const SelectGroup = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
`
